import { useTranslation } from 'react-i18next';
import { ContactForm } from '@/components/ContactForm';
import { Card, CardContent } from '@/components/ui/card';
import { Mail, MapPin, Clock } from 'lucide-react';
import { SEO, getBreadcrumbSchema, getLocalBusinessSchema } from '@/hooks/useSEO';
import { useLanguage } from '@/hooks/useLanguage';

export default function ContactPage() {
  const { t } = useTranslation();
  const { lang } = useLanguage();

  return (
    <div className="container py-12 lg:py-16">
      <SEO
        title={t('contact.title')}
        description={t('meta.contact')}
        path="/kontakt"
        jsonLd={[
          getBreadcrumbSchema([
            { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
            { name: t('contact.title'), url: `https://postnummerservice.se/${lang}/kontakt` },
          ]),
          getLocalBusinessSchema(),
        ]}
      />
      <div className="max-w-3xl mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          {t('contact.title')}
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed">
          {t('contact.intro')}
        </p>
      </div>

      <div className="grid gap-12 lg:grid-cols-3">
        {/* Contact Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-6 md:p-8">
              <ContactForm />
            </CardContent>
          </Card>
        </div>

        {/* Contact Info Sidebar */}
        <aside className="lg:col-span-1">
          <div className="sticky top-24 space-y-6">
            <Card>
              <CardContent className="p-6 space-y-5">
                <h2 className="font-semibold text-lg">{t('contact.info.title')}</h2>
                
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">Postnummerservice Norden AB</p>
                    <p className="text-sm text-muted-foreground">Biblioteksgatan 29</p>
                    <p className="text-sm text-muted-foreground">114 35 Stockholm</p>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('contact.info.generalEmail')}</p>
                      <a 
                        href="mailto:info@postnummerservice.se" 
                        className="text-sm font-medium hover:text-primary transition-colors"
                      >
                        info@postnummerservice.se
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('contact.info.supportEmail')}</p>
                      <a 
                        href="mailto:support@postnummerservice.se" 
                        className="text-sm font-medium hover:text-primary transition-colors"
                      >
                        support@postnummerservice.se
                      </a>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4 flex items-start gap-3">
                  <Clock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">{t('contact.info.hours')}</p>
                    <p className="text-sm text-muted-foreground">{t('contact.info.hoursValue')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-6">
                <h2 className="font-semibold mb-2">{t('contact.quickResponse.title')}</h2>
                <p className="text-sm text-muted-foreground">
                  {t('contact.quickResponse.description')}
                </p>
              </CardContent>
            </Card>
          </div>
        </aside>
      </div>
    </div>
  );
}
